import { AsyncImage } from "../../image";
import { Box } from "../../box";
import { forwardRef, useMemo } from "react";
import { Img } from "../../html";
import { createSizeAdapter, cssModule, isNil, isNilOrEmpty, isString, mergeProps, normalizeSize, omitProps, slot } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { Text } from "../../typography";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "div";

function AvatarImage({
  "aria-label": ariaLabel,
  name,
  retryCount,
  size,
  src
}) {
  if (!isString(src)) {
    return /*#__PURE__*/_jsx(Img, {
      alt: name,
      className: "o-ui-avatar-image",
      src: src
    });
  }

  return /*#__PURE__*/_jsx(AsyncImage, {
    alt: name,
    className: "o-ui-avatar-image",
    retryCount: retryCount,
    src: src,
    children: /*#__PURE__*/_jsx(AvatarInitials, {
      "aria-label": ariaLabel,
      name: name,
      size: size
    })
  });
}

const O365InitialsColorsForName = ["#99B433", "#6BA5E7", "#E773BD", "#00A300", "#1E7145", "#FF0097", "#7E3878", "#603CBA", "#5E4B8B", "#00ABA9", "#2D89EF", "#2B5797", "#DA532C", "#B91D47"];

function AvatarInitials({
  "aria-label": ariaLabel,
  name,
  size
}) {
  const initials = useMemo(() => {
    const cleanName = name.replace(/\s+/g, " ").trim();
    const [firstName, lastName] = cleanName.split(" ");
    return !isNil(firstName) && !isNil(lastName) ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0);
  }, [name]);
  const color = useMemo(() => {
    let hashCode = 0;

    for (let i = name.length - 1; i >= 0; i--) {
      const character = name.charCodeAt(i);
      const shift = i % 8;
      hashCode ^= (character << shift) + (character >> 8 - shift);
    }

    return O365InitialsColorsForName[hashCode % O365InitialsColorsForName.length];
  }, [name]);
  return /*#__PURE__*/_jsx(AvatarText, {
    "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : name,
    role: "img",
    size: size,
    style: {
      backgroundColor: color
    },
    children: initials
  });
}
/* eslint-disable sort-keys, sort-keys-fix/sort-keys-fix */


const textSize = createSizeAdapter({
  "2xs": "xs",
  "xs": "xs",
  "sm": "sm",
  "md": "md",
  "lg": "lg",
  "xl": "xl",
  "2xl": "2xl"
});
/* eslint-enable sort-keys, sort-keys-fix/sort-keys-fix */

export function AvatarText(props) {
  const {
    children,
    size,
    ...rest
  } = omitProps(props, ["src"]);
  return /*#__PURE__*/_jsx(Text, { ...mergeProps(rest, {
      className: "o-ui-avatar-text",
      size: textSize(size)
    }),
    children: children
  });
}
export function InnerAvatar({
  "aria-label": ariaLabel,
  as = DefaultElement,
  forwardedRef,
  name,
  retryCount,
  size,
  src,
  ...rest
}) {
  const sizeValue = useResponsiveValue(size);
  const content = !isNilOrEmpty(src) ? /*#__PURE__*/_jsx(AvatarImage, {
    "aria-label": ariaLabel,
    name: name,
    retryCount: retryCount,
    size: sizeValue,
    src: src
  }) : /*#__PURE__*/_jsx(AvatarInitials, {
    "aria-label": ariaLabel,
    name: name,
    size: sizeValue
  });
  return /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
      as,
      className: cssModule("o-ui-avatar", normalizeSize(sizeValue)),
      ref: forwardedRef
    }),
    children: content
  });
}
InnerAvatar.defaultElement = DefaultElement;
export const Avatar = slot("avatar", /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerAvatar, { ...props,
  forwardedRef: ref
})));